import { useCallback, useEffect, useMemo, useState } from "react";

import { deleteLiveStream, fetchLiveStreams, startLiveStream, stopLiveStream } from "@/features/admin/api";
import type { AdminLiveStream } from "@/features/admin/types";
import { useAdminSession } from "@/features/admin/useAdminSession";

import PaginationControls from "./PaginationControls";

const EMPTY_FORM = {
  title: "",
  sourceUrl: "",
  badgeLabel: "Live en Direct"
};
const HISTORY_PAGE_SIZE = 2;

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

export default function LivesTab() {
  const { request } = useAdminSession();
  const [streams, setStreams] = useState<AdminLiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [historyPage, setHistoryPage] = useState(1);

  const loadStreams = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextStreams = await fetchLiveStreams(request);
      setStreams(nextStreams);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les lives";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    void loadStreams();
  }, [loadStreams]);

  const activeStream = useMemo(() => streams.find((stream) => stream.isActive) ?? null, [streams]);
  const historyStreams = useMemo(() => streams.filter((stream) => !stream.isActive), [streams]);
  const historyTotalPages = Math.max(1, Math.ceil(historyStreams.length / HISTORY_PAGE_SIZE));
  const paginatedHistoryStreams = useMemo(() => {
    const startIndex = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return historyStreams.slice(startIndex, startIndex + HISTORY_PAGE_SIZE);
  }, [historyPage, historyStreams]);

  useEffect(() => {
    setHistoryPage((current) => Math.min(current, historyTotalPages));
  }, [historyTotalPages]);

  const handleStartStream = async () => {
    if (!form.title.trim() || !form.sourceUrl.trim()) {
      setError("Le titre et le lien du live sont obligatoires.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      await startLiveStream(request, {
        title: form.title.trim(),
        sourceUrl: form.sourceUrl.trim(),
        badgeLabel: form.badgeLabel.trim() || "Live en Direct"
      });
      setForm(EMPTY_FORM);
      setSuccess("Le live a bien été lancé et il est maintenant visible sur la page d'accueil.");
      await loadStreams();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de lancer ce live";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStopStream = async (streamId: string) => {
    setStoppingId(streamId);
    setError(null);
    setSuccess(null);

    try {
      await stopLiveStream(request, streamId);
      setSuccess("Le live en cours a été arrêté.");
      await loadStreams();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible d'arrêter ce live";
      setError(message);
    } finally {
      setStoppingId(null);
    }
  };

  const handleDeleteStream = async (streamId: string) => {
    setDeletingId(streamId);
    setError(null);
    setSuccess(null);

    try {
      await deleteLiveStream(request, streamId);
      setSuccess("Le live historique a été supprimé.");
      await loadStreams();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de supprimer ce live";
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Lives en Direct</h1>
          <p className="text-sm text-gray-500 mt-1">
            Lance un live public depuis un lien YouTube, TikTok ou une autre plateforme compatible iframe.
          </p>
        </div>
        <button
          onClick={() => void loadStreams()}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-medium text-gray-600 hover:border-[#E8521A] hover:text-[#E8521A] transition-colors whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          Actualiser
        </button>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      ) : null}

      {success ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-[420px_minmax(0,1fr)] gap-6">
        <div className="bg-white rounded-[28px] border border-gray-100 p-6 shadow-[0_24px_80px_rgba(17,24,39,0.05)]">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center">
              <i className="ri-live-line text-2xl"></i>
            </div>
            <div>
              <h2 className="font-bold text-[#1A1A1A]">Lancer un nouveau live</h2>
              <p className="text-sm text-gray-500">Un seul live actif est visible à la fois sur la home et la page publique.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Titre du live</label>
              <input
                type="text"
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                placeholder="Ex: Finale inter-entreprises ASC"
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Lien source</label>
              <input
                type="url"
                value={form.sourceUrl}
                onChange={(event) => setForm((current) => ({ ...current, sourceUrl: event.target.value }))}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Texte du badge</label>
              <input
                type="text"
                value={form.badgeLabel}
                onChange={(event) => setForm((current) => ({ ...current, badgeLabel: event.target.value }))}
                placeholder="Live en Direct"
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
              />
            </div>

            <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-[#9A3E18]">
              Pour un affichage intégré le plus fiable, privilégie un lien YouTube. Les autres plateformes sont tentées dans un iframe si elles l'autorisent.
            </div>

            <button
              onClick={() => void handleStartStream()}
              disabled={submitting}
              className="w-full py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? "Lancement..." : "Lancer le live"}
            </button>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-[linear-gradient(135deg,#111111_0%,#2B2B2B_100%)] rounded-[28px] border border-white/5 p-6 text-white shadow-[0_24px_80px_rgba(17,24,39,0.08)]">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-red-300 font-semibold">Etat courant</p>
                <h2 className="mt-3 text-2xl font-bold">
                  {activeStream ? activeStream.title : "Aucun live actif"}
                </h2>
                <p className="mt-2 text-sm text-white/70">
                  {activeStream
                    ? `Diffusé via ${activeStream.provider} depuis le ${formatDateTime(activeStream.startedAt)}`
                    : "Dès qu'un live est lancé, la home affichera un badge clignotant et la page /live diffusera le flux."}
                </p>
              </div>

              {activeStream ? (
                <button
                  onClick={() => void handleStopStream(activeStream.id)}
                  disabled={stoppingId === activeStream.id}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse"></span>
                  {stoppingId === activeStream.id ? "Arrêt..." : "Arrêter le live"}
                </button>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm text-white/80">
                  <i className="ri-live-line"></i>
                  Prêt à diffuser
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-[28px] border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[#1A1A1A]">Historique des lives</h2>
              <span className="text-sm text-gray-400">{historyStreams.length} enregistrement(s)</span>
            </div>

            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-24 rounded-2xl bg-gray-50 animate-pulse"></div>
                ))}
              </div>
            ) : historyStreams.length > 0 ? (
              <div className="space-y-3">
                {paginatedHistoryStreams.map((stream) => (
                  <div key={stream.id} className="rounded-2xl border border-gray-100 px-4 py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-[#1A1A1A]">{stream.title}</h3>
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                              stream.isActive ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {stream.isActive ? <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span> : null}
                            {stream.isActive ? "Actif" : "Terminé"}
                          </span>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-50 text-[#E8521A] text-xs font-medium">
                            {stream.provider}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-500 break-all">{stream.sourceUrl}</p>
                        <div className="mt-2 text-xs text-gray-400">
                          Démarré le {formatDateTime(stream.startedAt)}
                          {stream.endedAt ? ` · Arrêté le ${formatDateTime(stream.endedAt)}` : ""}
                        </div>
                      </div>

                      <button
                        onClick={() => void handleDeleteStream(stream.id)}
                        disabled={deletingId === stream.id}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                        title="Supprimer cet historique"
                      >
                        <i className={`${deletingId === stream.id ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}`}></i>
                        {deletingId === stream.id ? "Suppression..." : "Supprimer"}
                      </button>
                    </div>
                  </div>
                ))}
                <PaginationControls
                  currentPage={historyPage}
                  totalPages={historyTotalPages}
                  onPageChange={setHistoryPage}
                  itemLabel="historique des lives"
                />
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 px-6 py-12 text-center">
                <i className="ri-live-line text-4xl text-gray-300"></i>
                <p className="mt-4 text-sm text-gray-500">Aucun live n'a encore été lancé depuis l'administration.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
